import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useEstablishmentTechnicalProfessional } from '@/features/maintenance/establishmentTechnicalProfessionals/hooks/useEstablishmentTechnicalProfessional'
import { useUpdateEstablishmentTechnicalProfessional } from '@/features/maintenance/establishmentTechnicalProfessionals/hooks/useUpdateEstablishmentTechnicalProfessional'
import {
  establishmentTechnicalProfessionalCreateSchema,
  EstablishmentTechnicalProfessionalValidationSchema,
} from '@/features/maintenance/establishmentTechnicalProfessionals/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRightIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

const Update = () => {
  const { id } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToUpdateEstablishmentTechnicalProfessional =
    isUserPermissionsPending || hasUserPermission('Admin::Maintenance::EstablishmentTechnicalProfessionals::Update')

  const { data: establishmentTechnicalProfessional, isPending: establishmentTechnicalProfessionalIsPending } =
    useEstablishmentTechnicalProfessional(id || '')

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<EstablishmentTechnicalProfessionalValidationSchema>({
    resolver: zodResolver(establishmentTechnicalProfessionalCreateSchema),
    defaultValues: {
      display_name: establishmentTechnicalProfessional?.display_name,
    },
  })

  const {
    mutate: updateEstablishmentTechnicalProfessional,
    isPending: updateEstablishmentTechnicalProfessionalIsPending,
  } = useUpdateEstablishmentTechnicalProfessional({
    id: id || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentTechnicalProfessional', id] })
      goBack({ fallback: `/establishment-technical-professionals/${id}` })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<EstablishmentTechnicalProfessionalValidationSchema> = (data) =>
    updateEstablishmentTechnicalProfessional(data)

  useEffect(() => {
    if (establishmentTechnicalProfessionalIsPending) return

    reset({
      display_name: establishmentTechnicalProfessional?.display_name,
    })
  }, [establishmentTechnicalProfessionalIsPending])

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={userHasPermissionToUpdateEstablishmentTechnicalProfessional ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="false" className="flex flex-col gap-y-6">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/establishment-technical-professionals/${id}`} />

            <p className="text-white-400 font-normal">Especialidade Técnica</p>

            <ChevronsRightIcon className="text-white-400" />

            <h2>{establishmentTechnicalProfessional?.display_name}</h2>
          </div>

          <div className="flex gap-4">
            <Button
              variant="outline-error"
              onClick={() => goBack({ fallback: `/establishment-technical-professionals/${id}` })}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              name="update_establishment_technical_professional"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={updateEstablishmentTechnicalProfessionalIsPending}
              isLoading={updateEstablishmentTechnicalProfessionalIsPending}
            >
              Salvar
            </Button>
          </div>
        </div>

        {establishmentTechnicalProfessionalIsPending ? (
          <Box>Carregando...</Box>
        ) : (
          <Box className="max-h-[calc(100vh-128px)] flex flex-col gap-4 overflow-y-auto">
            <p className="text-lg font-bold">Especialidade Técnica</p>

            <TextField
              label="Nome"
              markAsRequired
              {...register('display_name')}
              errorMessage={errors?.display_name?.message}
              disabled={updateEstablishmentTechnicalProfessionalIsPending}
            />
          </Box>
        )}

        {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
